import { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { Card, inputCls } from "@/components/portal/shared";

export function TeamCard() {
  const { api } = usePortalAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "installer" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/users").then((r) => setData(r.data)).catch(() => setData({ users: [], roles: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/users", form);
      setForm({ name: "", email: "", password: "", role: "installer" });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Team Members" testid="team-card">
      <p className="text-xs text-slate-400 mb-6 -mt-1">Create accounts for office staff and fitters. Installers only see their own job sheet.</p>
      {data === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-navy-900" />
      ) : (
        <>
          <ul className="divide-y divide-warm-200 mb-6">
            {data.users.map((u) => (
              <li key={u.id} data-testid={`team-user-${u.id}`} className="py-3 flex items-center gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <span className="ml-auto rounded-full bg-warm-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">{u.role}</span>
              </li>
            ))}
          </ul>
          <form onSubmit={add} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Full name" aria-label="Full name" data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <select aria-label="Role" data-testid="team-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                {(data.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <input required type="email" placeholder="Email" aria-label="Email" data-testid="team-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input required type="password" minLength={8} placeholder="Temporary password (min 8 characters)" aria-label="Password" data-testid="team-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            {error && <p role="alert" data-testid="team-error" className="text-sm text-red-700">{error}</p>}
            <button type="submit" data-testid="team-add-btn" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 py-3 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add Team Member
            </button>
          </form>
        </>
      )}
    </Card>
  );
}
